import { Controller } from "@hotwired/stimulus"
import SignaturePad from "signature_pad"

// Captures a handwritten signature on a per-field canvas and serializes it to
// an SVG data URL in the hidden input as the user draws. Mounted on a wrapper
// element (not the form) so a single page can host multiple independent
// signatures, including ones inside conditional cards that start hidden.
export default class extends Controller {
  static targets = ["canvas", "input"]
  static values = { existing: String }

  connect() {
    this.pad = new SignaturePad(this.canvasTarget)
    // Persist to the hidden input after every completed stroke, so submission
    // never depends on a form-level save handler firing first.
    this.pad.addEventListener("endStroke", () => this.save())
    // Re-size whenever the canvas gains or loses layout size — covers viewport
    // changes and conditional cards revealing a previously-hidden (0-width)
    // canvas. ResizeObserver reports display:none as 0×0 and fires when the
    // card becomes visible.
    this.observer = new ResizeObserver(() => this.resize())
    this.observer.observe(this.canvasTarget)
    this.resize()
  }

  disconnect() {
    this.observer?.disconnect()
  }

  // Scale the canvas backing store to device pixel ratio so strokes are crisp
  // and pointer coordinates line up with what the user draws. No-op while
  // hidden (0 width); the ResizeObserver re-runs this once the canvas is
  // revealed and has width.
  resize() {
    const canvas = this.canvasTarget
    if (!canvas.offsetWidth) return
    const ratio = Math.max(window.devicePixelRatio || 1, 1)
    const data = this.pad.toData()
    canvas.width = canvas.offsetWidth * ratio
    canvas.height = (canvas.offsetHeight || 160) * ratio
    canvas.getContext("2d").scale(ratio, ratio)
    this.pad.clear()
    // Preserve a freshly-drawn signature across resizes; if nothing has been
    // drawn yet, paint the persisted signature (edit mode) into the box.
    if (data.length) {
      this.pad.fromData(data)
    } else if (this.hasExistingValue && this.existingValue) {
      this.pad.fromDataURL(this.existingValue)
    }
  }

  clear() {
    this.pad.clear()
    this.inputTarget.value = ""
    // Drop the persisted signature so a later resize doesn't repaint it after
    // the user deliberately cleared the box to re-sign.
    this.existingValue = ""
  }

  // Writes the SVG data URL only if something was drawn, so an untouched edit
  // keeps whatever value the hidden input was pre-filled with.
  save() {
    if (!this.pad.isEmpty()) {
      this.inputTarget.value = this.pad.toDataURL("image/svg+xml")
    }
  }
}
